import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Form, Table} from "react-bootstrap";
import AdminApi from "common/api";
import ModalDialog from "common/components/Modal";
import {AlertContext} from "context/AlertContext";

const WEEKDAYS = [
  {value: 1, label: "월"},
  {value: 2, label: "화"},
  {value: 3, label: "수"},
  {value: 4, label: "목"},
  {value: 5, label: "금"},
  {value: 6, label: "토"},
  {value: 7, label: "일"},
];

const EMPTY_FORM = {
  campaignCode: "",
  title: "",
  body: "",
  sendHour: "9",
  sendMinute: "30",
  weekdays: [],
  enabled: true,
  linkPath: "",
};

function parseWeekdays(value) {
  if (!value) return [];
  return value.split(",")
    .map((part) => Number(part.trim()))
    .filter((day) => day >= 1 && day <= 7);
}

function weekdayLabel(value) {
  return parseWeekdays(value)
    .map((day) => WEEKDAYS.find((item) => item.value === day)?.label)
    .filter(Boolean)
    .join(",");
}

const FAILURE_LABELS = {
  NO_TOKEN: "토큰 없음",
  UNREGISTERED: "토큰 만료 또는 앱 삭제",
  INVALID_ARGUMENT: "토큰 형식 오류",
  SENDER_ID_MISMATCH: "다른 Firebase 앱의 토큰",
  SEND_ERROR: "발송 요청 실패",
  QUOTA_EXCEEDED: "발송 한도 초과",
  UNAVAILABLE: "FCM 일시 오류",
  INTERNAL: "FCM 내부 오류",
  THIRD_PARTY_AUTH_ERROR: "APNs 인증 오류",
  UNKNOWN: "알 수 없는 오류",
};

function failureLabel(code) {
  return FAILURE_LABELS[code] || code || "알 수 없는 오류";
}

function summaryText(summary) {
  if (!Array.isArray(summary) || summary.length === 0) return "실패 없음";
  return summary.map((item) => `${failureLabel(item.code)} ${item.count}`).join(", ");
}

function FailureTable({failures}) {
  const rows = failures ?? [];
  if (rows.length === 0) {
    return <p className="push-note">실패한 회원이 없습니다.</p>;
  }
  return (
    <div className="push-failure-list">
      <Table bordered className="basic-table push-log-table">
        <thead>
          <tr>
            <th>회원번호</th>
            <th>사유</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, index) => (
            <tr key={`${item.userNo}-${item.code}-${index}`}>
              <td>{item.userNo}</td>
              <td>{failureLabel(item.code)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function lastSendText(campaign) {
  if (!campaign.lastSendDate) return "-";
  const status = campaign.lastStatus === "SENT" ? "완료" : campaign.lastStatus === "SENDING" ? "진행" : campaign.lastStatus;
  return `${campaign.lastSendDate} ${status} · 성공 ${campaign.lastSuccessCount ?? 0} / 실패 ${campaign.lastFailCount ?? 0}`;
}

function parseUserNos(text) {
  const parts = text.split(/[\s,]+/).map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return {userNos: []};
  const userNos = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) {
      return {error: "회원번호는 숫자만 입력할 수 있습니다."};
    }
    const userNo = Number(part);
    if (!Number.isSafeInteger(userNo) || userNo <= 0 || userNo > 2147483647) {
      return {error: "회원번호가 올바르지 않습니다."};
    }
    userNos.push(userNo);
  }
  return {userNos};
}

export default function PushTab() {
  const api = useMemo(() => new AdminApi(), []);
  const {setShow, setToastMsg} = useContext(AlertContext);

  const [campaigns, setCampaigns] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sendTarget, setSendTarget] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [searchUsers, setSearchUsers] = useState([]);
  const [searched, setSearched] = useState(false);
  const [manualUserNos, setManualUserNos] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [directLogs, setDirectLogs] = useState([]);
  const [manualLogs, setManualLogs] = useState([]);
  const [latestSend, setLatestSend] = useState(null);
  const [latestBroadcast, setLatestBroadcast] = useState(null);
  const [openLogNo, setOpenLogNo] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const toast = useCallback((message) => {
    setToastMsg(message);
    setShow(true);
  }, [setShow, setToastMsg]);

  const loadCampaigns = useCallback(() => {
    api.getPushCampaigns().then((res) => {
      if (res?.result === "ok") {
        setCampaigns(res.campaigns ?? []);
      } else {
        setCampaigns([]);
        toast(res?.message || "캠페인 목록을 불러오지 못했습니다.");
      }
    });
  }, [api, toast]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  function openCreate() {
    setFormMode("create");
    setForm(EMPTY_FORM);
  }

  function openEdit(campaign) {
    const [hour, minute] = (campaign.sendTime || "09:30").split(":");
    setFormMode("edit");
    setForm({
      campaignCode: campaign.campaignCode,
      title: campaign.title ?? "",
      body: campaign.body ?? "",
      sendHour: String(Number(hour)),
      sendMinute: String(Number(minute)),
      weekdays: parseWeekdays(campaign.weekdays),
      enabled: campaign.enabled !== false,
      linkPath: campaign.linkPath ?? "",
    });
  }

  const loadDirectLogs = useCallback((campaignCode) => {
    api.getPushDirectLogs(campaignCode).then((res) => {
      if (res?.result === "ok") {
        setDirectLogs(res.logs ?? []);
      } else {
        setDirectLogs([]);
        toast(res?.message || "개별 발송 로그를 불러오지 못했습니다.");
      }
    });
  }, [api, toast]);

  const loadManualLogs = useCallback((campaignCode) => {
    api.getPushBroadcastLogs(campaignCode).then((res) => {
      if (res?.result === "ok") {
        setManualLogs(res.logs ?? []);
      } else {
        setManualLogs([]);
        toast(res?.message || "전체 발송 로그를 불러오지 못했습니다.");
      }
    });
  }, [api, toast]);

  function openSend(campaign) {
    setSendTarget(campaign);
    setKeyword("");
    setSearchUsers([]);
    setSearched(false);
    setManualUserNos("");
    setRecipients([]);
    setLatestSend(null);
    setLatestBroadcast(null);
    setOpenLogNo(null);
    setDirectLogs([]);
    setManualLogs([]);
    loadDirectLogs(campaign.campaignCode);
    loadManualLogs(campaign.campaignCode);
  }

  function toggleWeekday(day) {
    setForm((prev) => {
      const exists = prev.weekdays.includes(day);
      const weekdays = exists
        ? prev.weekdays.filter((item) => item !== day)
        : [...prev.weekdays, day].sort((a, b) => a - b);
      return {...prev, weekdays};
    });
  }

  function buildPayload() {
    const sendHour = Number(form.sendHour);
    const sendMinute = Number(form.sendMinute);
    if (!Number.isInteger(sendHour) || sendHour < 0 || sendHour > 23) {
      return {error: "발송 시는 0부터 23까지입니다."};
    }
    if (!Number.isInteger(sendMinute) || sendMinute < 0 || sendMinute > 59) {
      return {error: "발송 분은 0부터 59까지입니다."};
    }
    if (!form.title.trim() || !form.body.trim()) {
      return {error: "제목과 본문을 입력해주세요."};
    }
    if (form.weekdays.length === 0) {
      return {error: "발송 요일을 선택해주세요."};
    }
    if (formMode === "create" && !/^[a-z0-9][a-z0-9_-]{0,63}$/.test(form.campaignCode.trim())) {
      return {error: "코드는 영문 소문자, 숫자, 하이픈만 사용할 수 있습니다."};
    }
    const linkPath = form.linkPath.trim();
    if (linkPath && !/^\/[a-z0-9][a-z0-9/_-]{0,180}$/.test(linkPath)) {
      return {error: "이동 경로는 /playground 처럼 입력해주세요."};
    }
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      linkPath,
      sendHour,
      sendMinute,
      weekdays: form.weekdays.join(","),
      enabled: form.enabled,
    };
    if (formMode === "create") {
      payload.campaignCode = form.campaignCode.trim();
    }
    return {payload};
  }

  function onSave() {
    const {payload, error} = buildPayload();
    if (error) {
      toast(error);
      setConfirm(null);
      return;
    }
    const request = formMode === "create"
      ? api.createPushCampaign(payload)
      : api.updatePushCampaign(form.campaignCode, payload);
    request.then((res) => {
      if (res?.result === "ok") {
        toast(formMode === "create" ? "캠페인을 등록했습니다." : "캠페인을 저장했습니다.");
        setFormMode(null);
        loadCampaigns();
        if (sendTarget && res.campaign?.campaignCode === sendTarget.campaignCode) {
          setSendTarget(res.campaign);
        }
      } else {
        toast(res?.message || "저장에 실패했습니다.");
      }
    }).finally(() => setConfirm(null));
  }

  function onToggleEnabled(campaign) {
    api.updatePushCampaign(campaign.campaignCode, {enabled: !campaign.enabled}).then((res) => {
      if (res?.result === "ok") {
        toast(campaign.enabled ? "이후 발송을 중지했습니다." : "발송을 다시 켰습니다.");
        loadCampaigns();
        if (sendTarget?.campaignCode === campaign.campaignCode && res.campaign) {
          setSendTarget(res.campaign);
        }
      } else {
        toast(res?.message || "변경에 실패했습니다.");
      }
    }).finally(() => setConfirm(null));
  }

  function addRecipients(users) {
    setRecipients((prev) => {
      const next = [...prev];
      users.forEach((user) => {
        if (!next.some((item) => item.userNo === user.userNo)) {
          next.push(user);
        }
      });
      return next;
    });
  }

  function onSearchUsers(e) {
    e.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) {
      toast("닉네임 또는 회원번호를 입력해주세요.");
      return;
    }
    api.getAdminUsers({keyword: trimmed, page: 1, countPerPage: 20}).then((res) => {
      setSearched(true);
      setSearchUsers(res?.result === "ok" ? (res.users ?? []) : []);
    });
  }

  function onAddManualUsers() {
    const {userNos, error} = parseUserNos(manualUserNos);
    if (error) {
      toast(error);
      return;
    }
    if (userNos.length === 0) {
      toast("추가할 회원번호를 입력해주세요.");
      return;
    }
    addRecipients(userNos.map((userNo) => ({userNo, nickname: ""})));
    setManualUserNos("");
  }

  function onSend() {
    if (!sendTarget) return;
    if (recipients.length === 0) {
      toast("보낼 회원을 선택해주세요.");
      setConfirm(null);
      return;
    }
    if (recipients.length > 1000) {
      toast("한 번에 1000명까지 보낼 수 있습니다.");
      setConfirm(null);
      return;
    }
    api.sendPushCampaign(sendTarget.campaignCode, recipients.map((user) => user.userNo)).then((res) => {
      if (res?.result === "ok") {
        setLatestSend(res);
        const saved = res.logSaved === false ? " 이력 저장에는 실패했습니다." : "";
        toast(`발송했습니다. 성공 ${res.successCount ?? 0}건, 실패 ${res.failCount ?? 0}건, 토큰 없음 ${res.skippedUserCount ?? 0}명. ${summaryText(res.errorSummary)}.${saved}`);
        setRecipients([]);
        loadDirectLogs(sendTarget.campaignCode);
        loadCampaigns();
      } else {
        toast(res?.message || "발송에 실패했습니다.");
      }
    }).finally(() => setConfirm(null));
  }

  function onBroadcast() {
    if (!sendTarget) return;
    api.broadcastPushCampaign(sendTarget.campaignCode).then((res) => {
      if (res?.result === "ok") {
        setLatestBroadcast(res);
        const saved = res.logSaved === false ? " 이력 저장에는 실패했습니다." : "";
        toast(`전체 발송했습니다. 성공 ${res.successCount ?? 0}건, 실패 ${res.failCount ?? 0}건.${saved}`);
        loadManualLogs(sendTarget.campaignCode);
        loadCampaigns();
      } else {
        toast(res?.message || "전체 발송에 실패했습니다.");
      }
    }).finally(() => setConfirm(null));
  }

  function onDelete() {
    if (formMode !== "edit" || !form.campaignCode) return;
    api.deletePushCampaign(form.campaignCode).then((res) => {
      if (res?.result === "ok") {
        toast("캠페인을 목록에서 뺐습니다.");
        if (sendTarget?.campaignCode === form.campaignCode) {
          setSendTarget(null);
        }
        setFormMode(null);
        loadCampaigns();
      } else {
        toast(res?.message || "삭제에 실패했습니다.");
      }
    }).finally(() => setConfirm(null));
  }

  return (
    <div className="push-admin">
      <h1>푸시 캠페인</h1>
      <div className="push-toolbar">
        <button type="button" className="blue-btn" onClick={openCreate}>새 캠페인</button>
      </div>

      {formMode &&
        <section className="push-form">
          <h2>{formMode === "create" ? "캠페인 등록" : "캠페인 수정"}</h2>
          <div className="push-form-grid">
            <span>코드</span>
            <Form.Control
              value={form.campaignCode}
              disabled={formMode === "edit"}
              placeholder="morning-weekday"
              onChange={(e) => setForm({...form, campaignCode: e.target.value})}
            />
            <span>제목</span>
            <Form.Control
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
            />
            <span>본문</span>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.body}
              onChange={(e) => setForm({...form, body: e.target.value})}
            />
            <span>이동 경로</span>
            <Form.Control
              value={form.linkPath}
              placeholder="/playground"
              onChange={(e) => setForm({...form, linkPath: e.target.value})}
            />
            <span>발송 시각</span>
            <div className="push-time-row">
              <Form.Control
                type="number"
                min={0}
                max={23}
                value={form.sendHour}
                onChange={(e) => setForm({...form, sendHour: e.target.value})}
                style={{width: 90}}
              />
              <span>시</span>
              <Form.Control
                type="number"
                min={0}
                max={59}
                value={form.sendMinute}
                onChange={(e) => setForm({...form, sendMinute: e.target.value})}
                style={{width: 90}}
              />
              <span>분</span>
            </div>
            <span>요일</span>
            <div className="push-weekdays">
              {WEEKDAYS.map((day) => (
                <Form.Check
                  key={day.value}
                  type="checkbox"
                  id={`push-weekday-${day.value}`}
                  label={day.label}
                  checked={form.weekdays.includes(day.value)}
                  onChange={() => toggleWeekday(day.value)}
                />
              ))}
            </div>
            <span>사용</span>
            <Form.Check
              type="switch"
              id="push-enabled"
              label={form.enabled ? "발송" : "중지"}
              checked={form.enabled}
              onChange={(e) => setForm({...form, enabled: e.target.checked})}
            />
          </div>
          <p className="push-note">발송 시각은 한국 시간입니다. 이동 경로를 넣으면 알림을 눌렀을 때 그 화면으로 엽니다. 게릴라 발송은 중지로 저장한 뒤 발송 화면의 전체 발송을 사용합니다.</p>
          <div className="push-actions">
            <button type="button" className="blue-btn" onClick={() => setConfirm({type: "save"})}>저장</button>
            {formMode === "edit" &&
              <button type="button" className="red-btn" onClick={() => setConfirm({type: "delete"})}>삭제</button>
            }
            <button type="button" className="grey-btn" onClick={() => setFormMode(null)}>취소</button>
          </div>
        </section>
      }

      <Table bordered className="basic-table">
        <thead>
          <tr>
            <th>코드</th>
            <th>제목</th>
            <th>본문</th>
            <th>이동</th>
            <th>시각</th>
            <th>요일</th>
            <th>상태</th>
            <th>마지막 전체 발송</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.length > 0 ? campaigns.map((campaign) => (
            <tr key={campaign.campaignCode}>
              <td>{campaign.campaignCode}</td>
              <td className="push-title-cell" title={campaign.title}>{campaign.title}</td>
              <td className="push-body-cell" title={campaign.body}>{campaign.body}</td>
              <td>{campaign.linkPath || "-"}</td>
              <td>{campaign.sendTime}</td>
              <td>{campaign.weekdayLabel || weekdayLabel(campaign.weekdays)}</td>
              <td>{campaign.enabled ? "발송" : "중지"}</td>
              <td>{lastSendText(campaign)}</td>
              <td>
                <div className="push-actions push-table-actions">
                  <button type="button" className="blue-btn" onClick={() => openEdit(campaign)}>수정</button>
                  <button type="button" className="green-btn" onClick={() => openSend(campaign)}>발송</button>
                  <button
                    type="button"
                    className={campaign.enabled ? "red-btn" : "grey-btn"}
                    onClick={() => setConfirm({type: "toggle", campaign})}
                  >
                    {campaign.enabled ? "중지" : "재개"}
                  </button>
                </div>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan={9} style={{textAlign: "center"}}>등록된 캠페인이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </Table>

      {sendTarget &&
        <section className="push-send">
          <h2>발송</h2>
          <p>{sendTarget.title}</p>
          <p className="push-note">{sendTarget.body}</p>
          <div className="push-actions">
            <button type="button" className="blue-btn" onClick={() => setConfirm({type: "broadcast"})}>전체 발송</button>
          </div>
          <p className="push-note">토큰이 있는 전체 회원에게 바로 보냅니다. 캠페인이 중지여도 이 발송은 나갑니다. 예약 발송은 중지 동안 나가지 않으니, 게릴라는 중지를 유지하세요.</p>
          {latestBroadcast &&
            <div className="push-log">
              <h3>이번 전체 발송</h3>
              <p className="push-note">
                토큰 {latestBroadcast.tokenCount ?? 0} · 성공 {latestBroadcast.successCount ?? 0} · 실패 {latestBroadcast.failCount ?? 0}
              </p>
              <p className="push-note">{summaryText(latestBroadcast.errorSummary)}</p>
              {latestBroadcast.logSaved === false &&
                <p className="push-note">이번 결과는 화면에만 남았습니다. push_campaign_manual_log 테이블을 만든 뒤 다시 보내면 이력이 저장됩니다.</p>
              }
            </div>
          }
          <div className="push-log">
            <h3>최근 전체 발송</h3>
            <Table bordered className="basic-table push-log-table">
              <thead>
                <tr>
                  <th>시각</th>
                  <th>토큰</th>
                  <th>성공</th>
                  <th>실패</th>
                  <th>사유</th>
                </tr>
              </thead>
              <tbody>
                {manualLogs.length > 0 ? manualLogs.map((log) => (
                  <tr key={log.manualLogNo}>
                    <td>{log.createdAt}</td>
                    <td>{log.tokenCount}</td>
                    <td>{log.successCount}</td>
                    <td>{log.failCount}</td>
                    <td>{summaryText(log.errorSummary)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} style={{textAlign: "center"}}>전체 발송 이력이 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <h3 style={{marginTop: 24}}>특정 회원 발송</h3>
          <p className="push-note">캠페인이 중지여도 선택한 회원에게는 보낼 수 있습니다. 예약 발송만 중지 동안 나가지 않습니다.</p>

          <form className="push-send-search" onSubmit={onSearchUsers} style={{marginTop: 16}}>
            <Form.Control
              value={keyword}
              placeholder="닉네임 또는 회원번호"
              onChange={(e) => setKeyword(e.target.value)}
              style={{maxWidth: 280}}
            />
            <button type="submit" className="blue-btn">회원 검색</button>
          </form>
          {searched &&
            <Table bordered className="basic-table push-search-result">
              <thead>
                <tr>
                  <th>회원번호</th>
                  <th>닉네임</th>
                  <th>상태</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {searchUsers.length > 0 ? searchUsers.map((user) => (
                  <tr key={user.userNo}>
                    <td>{user.userNo}</td>
                    <td>{user.nickname || "-"}</td>
                    <td>{user.status || "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="grey-btn"
                        onClick={() => addRecipients([{userNo: user.userNo, nickname: user.nickname || ""}])}
                      >
                        추가
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} style={{textAlign: "center"}}>검색 결과가 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          }

          <div className="push-send-search" style={{marginTop: 16}}>
            <Form.Control
              value={manualUserNos}
              placeholder="회원번호 직접 입력. 쉼표 또는 줄바꿈"
              onChange={(e) => setManualUserNos(e.target.value)}
            />
            <button type="button" className="grey-btn" onClick={onAddManualUsers}>번호 추가</button>
          </div>

          <div className="push-recipients">
            {recipients.map((user) => (
              <span key={user.userNo} className="push-recipient">
                {user.nickname ? `${user.nickname} (${user.userNo})` : user.userNo}
                <button type="button" onClick={() => setRecipients(recipients.filter((item) => item.userNo !== user.userNo))}>×</button>
              </span>
            ))}
          </div>
          <p className="push-note">선택 {recipients.length}명. 한 번에 1000명까지 같은 문구를 보냅니다.</p>
          <div className="push-actions">
            <button
              type="button"
              className="blue-btn"
              disabled={recipients.length === 0}
              onClick={() => setConfirm({type: "send"})}
            >
              발송
            </button>
            <button type="button" className="grey-btn" onClick={() => setSendTarget(null)}>닫기</button>
          </div>

          <div className="push-log">
            {latestSend &&
              <>
                <h3>이번 발송</h3>
                <p className="push-note">
                  요청 {latestSend.requestedUserCount ?? 0}명 · 성공 {latestSend.successCount ?? 0} · 실패 {latestSend.failCount ?? 0} · 토큰 없음 {latestSend.skippedUserCount ?? 0}
                </p>
                <p className="push-note">{summaryText(latestSend.errorSummary)}</p>
                {latestSend.logSaved === false &&
                  <p className="push-note">이번 결과는 화면에만 남았습니다. push_campaign_direct_log 테이블을 만든 뒤 다시 보내면 이력이 저장됩니다.</p>
                }
                <FailureTable failures={latestSend.failures} />
              </>
            }
            <h3>최근 개별 발송</h3>
            <Table bordered className="basic-table push-log-table">
              <thead>
                <tr>
                  <th>시각</th>
                  <th>요청</th>
                  <th>성공</th>
                  <th>실패</th>
                  <th>토큰 없음</th>
                  <th>사유</th>
                </tr>
              </thead>
              <tbody>
                {directLogs.length > 0 ? directLogs.map((log) => (
                  <React.Fragment key={log.directLogNo}>
                    <tr className="push-log-row" onClick={() => setOpenLogNo(openLogNo === log.directLogNo ? null : log.directLogNo)}>
                      <td>{log.createdAt}</td>
                      <td>{log.requestedCount}</td>
                      <td>{log.successCount}</td>
                      <td>{log.failCount}</td>
                      <td>{log.skippedCount}</td>
                      <td>{summaryText(log.errorSummary)}</td>
                    </tr>
                    {openLogNo === log.directLogNo &&
                      <tr>
                        <td colSpan={6}>
                          <FailureTable failures={log.failures} />
                        </td>
                      </tr>
                    }
                  </React.Fragment>
                )) : (
                  <tr>
                    <td colSpan={6} style={{textAlign: "center"}}>개별 발송 이력이 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </Table>
            <p className="push-note">행을 누르면 실패하거나 토큰이 없는 회원 번호를 볼 수 있습니다.</p>
          </div>
        </section>
      }

      <ModalDialog
        show={confirm?.type === "save"}
        title="확인"
        btnText="저장"
        hasTwoButtons
        secondBtnText="취소"
        onHide={() => setConfirm(null)}
        onOk={onSave}
        onClickSecond={() => setConfirm(null)}
      >
        <p style={{textAlign: "center"}}>캠페인을 저장할까요?</p>
      </ModalDialog>
      <ModalDialog
        show={confirm?.type === "toggle"}
        title="확인"
        btnText={confirm?.campaign?.enabled ? "중지" : "재개"}
        hasTwoButtons
        secondBtnText="취소"
        onHide={() => setConfirm(null)}
        onOk={() => onToggleEnabled(confirm.campaign)}
        onClickSecond={() => setConfirm(null)}
      >
        <p style={{textAlign: "center"}}>
          {confirm?.campaign?.enabled
            ? "이후 예약 발송을 중지할까요? 이미 나간 알림은 되돌릴 수 없습니다."
            : "이 캠페인 발송을 다시 켤까요?"}
        </p>
      </ModalDialog>
      <ModalDialog
        show={confirm?.type === "send"}
        title="발송 확인"
        btnText="발송"
        hasTwoButtons
        secondBtnText="취소"
        onHide={() => setConfirm(null)}
        onOk={onSend}
        onClickSecond={() => setConfirm(null)}
      >
        <p style={{textAlign: "center"}}>
          {recipients.length}명에게 「{sendTarget?.title}」을 보낼까요?
        </p>
      </ModalDialog>
      <ModalDialog
        show={confirm?.type === "broadcast"}
        title="전체 발송 확인"
        btnText="전체 발송"
        hasTwoButtons
        secondBtnText="취소"
        onHide={() => setConfirm(null)}
        onOk={onBroadcast}
        onClickSecond={() => setConfirm(null)}
      >
        <p style={{textAlign: "center"}}>
          토큰이 있는 전체 회원에게 「{sendTarget?.title}」을 지금 보낼까요? 이미 나간 알림은 되돌릴 수 없습니다.
        </p>
      </ModalDialog>
      <ModalDialog
        show={confirm?.type === "delete"}
        title="삭제 확인"
        btnText="삭제"
        hasTwoButtons
        secondBtnText="취소"
        onHide={() => setConfirm(null)}
        onOk={onDelete}
        onClickSecond={() => setConfirm(null)}
      >
        <p style={{textAlign: "center"}}>
          「{form.title || form.campaignCode}」을 목록에서 뺄까요? 발송 이력은 남고, 이후 예약 발송과 전체 발송은 되지 않습니다.
        </p>
      </ModalDialog>
    </div>
  );
}
